import React, {useState, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Image,
  Platform,
  ImageSourcePropType,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {displayService} from '../services';
import {Display} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useRouting} from '../hooks/useRouting';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {getAllTemplateConfigs, DEFAULT_TEMPLATE_ID} from '../templates';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  SubHeading,
  BodyText,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';

const FONT_SCALE_MIN = 0.8;
const FONT_SCALE_MAX = 1.4;
const FONT_SCALE_STEP = 0.1;

const TEMPLATE_PREVIEWS: Record<string, ImageSourcePropType> = {
  classic: Platform.OS === 'web'
    ? {uri: '/templates/classic-preview.png'}
    : require('../assets/templates/classic-preview.jpg'),
  split: Platform.OS === 'web'
    ? {uri: '/templates/split-preview.png'}
    : require('../assets/templates/split-preview.jpg'),
};

export default function DisplayEditScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen, navigate} = useSettingsNavigation('DisplayEdit');
  const dispatch = useAppDispatch();
  const {getRouteParam} = useRouting();
  const displayId = getRouteParam('displayId') || '';

  const [display, setDisplay] = useState<Display | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Editable fields
  const [name, setName] = useState('');
  const [frameTemplateId, setFrameTemplateId] = useState(DEFAULT_TEMPLATE_ID);
  const [fontScale, setFontScale] = useState(1.0);
  const [photoCycleEnabled, setPhotoCycleEnabled] = useState(true);
  const [photoCycleIntervalMs, setPhotoCycleIntervalMs] = useState(30000);

  const templates = getAllTemplateConfigs();

  const loadDisplay = useCallback(async () => {
    const data = await displayService.getDisplay(displayId);
    setDisplay(data);
    setName(data.name);
    setFrameTemplateId(data.frameTemplateId);
    setFontScale(data.fontScale);
    setPhotoCycleEnabled(data.photoCycleEnabled);
    setPhotoCycleIntervalMs(data.photoCycleIntervalMs);
  }, [displayId]);

  const {isLoading, loadError} = useDataLoader(loadDisplay);

  const saveChanges = useCallback(
    async (updates: Partial<Display>) => {
      setIsSaving(true);
      setSaveSuccess(false);
      try {
        const updated = await displayService.updateDisplay(displayId, updates);
        setDisplay(updated);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      } catch (error) {
        console.error('Failed to save display', error);
      } finally {
        setIsSaving(false);
      }
    },
    [displayId],
  );

  const handleNameBlur = () => {
    if (name !== display?.name) {
      saveChanges({name});
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    if (templateId !== frameTemplateId) {
      setFrameTemplateId(templateId);
      saveChanges({frameTemplateId: templateId});
    }
  };

  const handleFontScaleDecrease = () => {
    const newValue = Math.max(
      FONT_SCALE_MIN,
      Math.round((fontScale - FONT_SCALE_STEP) * 10) / 10,
    );
    setFontScale(newValue);
    saveChanges({fontScale: newValue});
  };

  const handleFontScaleIncrease = () => {
    const newValue = Math.min(
      FONT_SCALE_MAX,
      Math.round((fontScale + FONT_SCALE_STEP) * 10) / 10,
    );
    setFontScale(newValue);
    saveChanges({fontScale: newValue});
  };

  const handlePhotoCycleToggle = () => {
    const newValue = !photoCycleEnabled;
    setPhotoCycleEnabled(newValue);
    saveChanges({photoCycleEnabled: newValue});
  };

  const handleIntervalChange = (seconds: number) => {
    const ms = seconds * 1000;
    setPhotoCycleIntervalMs(ms);
    saveChanges({photoCycleIntervalMs: ms});
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </SettingsLayout>
    );
  }

  if (!display) {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          {loadError === 'network' ? (
            <>
              <OfflineBanner />
              <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
            </>
          ) : (
            <BodyText>Display not found</BodyText>
          )}
        </View>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      currentScreen="displays"
      onNavigate={navigateToSettingsScreen}
      onLogout={handleLogout}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <OfflineBanner />
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigate('Displays')}
            activeOpacity={0.7}>
            <Text style={styles.backIcon}>arrow_back</Text>
            <Text style={styles.backText}>{t('displays.title')}</Text>
          </TouchableOpacity>
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <Heading>{t('displays.editDisplay')}</Heading>
              {display.isDefault && (
                <View style={styles.defaultBadge}>
                  <Text style={styles.defaultBadgeText}>{t('displays.default')}</Text>
                </View>
              )}
            </View>
            <TouchableOpacity
              style={styles.viewFrameButton}
              onPress={() => navigate('Frame', {displayId: display.id})}
              activeOpacity={0.7}>
              <Text style={styles.viewFrameIcon}>visibility</Text>
              <Text style={styles.viewFrameText}>{t('displays.viewFrame')}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Success Message */}
        {saveSuccess && (
          <View style={styles.successBanner}>
            <Text style={styles.successIcon}>check_circle</Text>
            <Text style={styles.successBannerText}>{t('displays.saved')}</Text>
          </View>
        )}

        {/* Display Name */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>
            {t('displays.displayName')}
          </SubHeading>
          <TextInput
            style={styles.nameInput}
            value={name}
            onChangeText={setName}
            onBlur={handleNameBlur}
            placeholder={t('displays.displayNamePlaceholder')}
            placeholderTextColor={theme.textMuted}
          />
        </View>

        {/* Quick Actions */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>Manage Content</SubHeading>
          <View style={styles.quickActionsRow}>
            <TouchableOpacity
              style={styles.quickActionCard}
              onPress={() =>
                navigate('DisplayPhotos', {displayId: display.id})
              }
              activeOpacity={0.7}>
              <Text style={styles.quickActionIcon}>photo_library</Text>
              <Text style={styles.quickActionLabel}>{t('displays.photos')}</Text>
              <Text style={styles.quickActionCount}>{display.photoCount === 0 ? t('displays.allPhotos') : display.photoCount}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionCard}
              onPress={() =>
                navigate('DisplayCalendars', {displayId: display.id})
              }
              activeOpacity={0.7}>
              <Text style={styles.quickActionIcon}>calendar_month</Text>
              <Text style={styles.quickActionLabel}>
                {t('displays.calendars')}
              </Text>
              <Text style={styles.quickActionCount}>{display.calendarCount === 0 ? t('displays.allCalendars') : display.calendarCount}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Template Selection */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>
            {t('display.selectTemplate')}
          </SubHeading>
          <BodyText style={styles.sectionDescription}>
            {t('display.selectTemplateDescription')}
          </BodyText>

          <View style={styles.templateGrid}>
            {templates.map(template => (
              <TouchableOpacity
                key={template.id}
                style={[
                  styles.templateCard,
                  frameTemplateId === template.id && styles.templateCardSelected,
                ]}
                onPress={() => handleTemplateSelect(template.id)}
                disabled={isSaving}
                activeOpacity={0.7}>
                <View style={styles.previewImageContainer}>
                  <Image
                    source={TEMPLATE_PREVIEWS[template.id]}
                    style={styles.previewImage}
                    resizeMode="cover"
                  />
                </View>
                <View style={styles.templateInfo}>
                  <Text
                    style={[
                      styles.templateName,
                      frameTemplateId === template.id &&
                        styles.templateNameSelected,
                    ]}>
                    {t(template.name)}
                  </Text>
                </View>
                {frameTemplateId === template.id && (
                  <View style={styles.selectedBadge}>
                    <Text style={styles.selectedIcon}>check_circle</Text>
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Font Size Section */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>
            {t('display.fontSize')}
          </SubHeading>
          <BodyText style={styles.sectionDescription}>
            {t('display.fontSizeDescription')}
          </BodyText>

          <GlassPanel style={styles.fontSizePanel}>
            <View style={styles.fontSizePreview}>
              <Text style={[styles.fontSizePreviewText, {fontSize: 24 * fontScale}]}>
                14:30
              </Text>
              <Text
                style={[styles.fontSizePreviewLabel, {fontSize: 14 * fontScale}]}>
                {t('display.previewText')}
              </Text>
            </View>

            <View style={styles.fontSizeControls}>
              <TouchableOpacity
                style={[
                  styles.fontSizeButton,
                  fontScale <= FONT_SCALE_MIN && styles.fontSizeButtonDisabled,
                ]}
                onPress={handleFontScaleDecrease}
                disabled={fontScale <= FONT_SCALE_MIN || isSaving}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.fontSizeButtonText,
                    fontScale <= FONT_SCALE_MIN &&
                      styles.fontSizeButtonTextDisabled,
                  ]}>
                  A-
                </Text>
              </TouchableOpacity>

              <View style={styles.fontScaleValue}>
                <Text style={styles.fontScaleValueText}>
                  {Math.round(fontScale * 100)}%
                </Text>
              </View>

              <TouchableOpacity
                style={[
                  styles.fontSizeButton,
                  fontScale >= FONT_SCALE_MAX && styles.fontSizeButtonDisabled,
                ]}
                onPress={handleFontScaleIncrease}
                disabled={fontScale >= FONT_SCALE_MAX || isSaving}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.fontSizeButtonTextLarge,
                    fontScale >= FONT_SCALE_MAX &&
                      styles.fontSizeButtonTextDisabled,
                  ]}>
                  A+
                </Text>
              </TouchableOpacity>
            </View>
          </GlassPanel>
        </View>

        {/* Photo Cycle Settings */}
        <View style={styles.section}>
          <SubHeading style={styles.sectionTitle}>
            {t('gallery.autoCycle')}
          </SubHeading>

          <GlassPanel style={styles.cyclePanel}>
            <TouchableOpacity
              style={styles.toggleRow}
              onPress={handlePhotoCycleToggle}
              activeOpacity={0.7}>
              <Text style={styles.toggleLabel}>{t('gallery.autoCycle')}</Text>
              <View
                style={[
                  styles.toggle,
                  photoCycleEnabled && styles.toggleEnabled,
                ]}>
                <View
                  style={[
                    styles.toggleKnob,
                    photoCycleEnabled && styles.toggleKnobEnabled,
                  ]}
                />
              </View>
            </TouchableOpacity>

            {photoCycleEnabled && (
              <View style={styles.intervalRow}>
                <Text style={styles.intervalLabel}>
                  {t('gallery.cycleInterval')}
                </Text>
                <View style={styles.intervalButtons}>
                  {[10, 30, 60, 120].map(seconds => (
                    <TouchableOpacity
                      key={seconds}
                      style={[
                        styles.intervalButton,
                        photoCycleIntervalMs === seconds * 1000 &&
                          styles.intervalButtonSelected,
                      ]}
                      onPress={() => handleIntervalChange(seconds)}
                      activeOpacity={0.7}>
                      <Text
                        style={[
                          styles.intervalButtonText,
                          photoCycleIntervalMs === seconds * 1000 &&
                            styles.intervalButtonTextSelected,
                        ]}>
                        {seconds}s
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </GlassPanel>
        </View>
      </ScrollView>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Header
  header: {
    marginBottom: 24,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  backIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },
  backText: {
    color: theme.primary,
    fontSize: 16,
  },
  defaultBadge: {
    alignSelf: 'flex-start',
    backgroundColor: theme.primaryLight,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginTop: 8,
  },
  defaultBadgeText: {
    color: theme.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  headerLeft: {
    flex: 1,
  },
  viewFrameButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: theme.primary,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  viewFrameIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: '#1c1917',
  },
  viewFrameText: {
    color: '#1c1917',
    fontSize: 13,
    fontWeight: '600',
  },

  // Success Banner
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    padding: 12,
    borderRadius: 12,
    marginBottom: 24,
  },
  successIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.success,
  },
  successBannerText: {
    fontSize: 14,
    color: theme.success,
    fontWeight: '500',
  },

  // Section
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    marginBottom: 8,
  },
  sectionDescription: {
    color: theme.textMuted,
    marginBottom: 16,
  },

  // Name Input
  nameInput: {
    backgroundColor: theme.surface,
    borderRadius: 12,
    padding: 16,
    color: theme.text,
    fontSize: 16,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },

  // Quick Actions
  quickActionsRow: {
    flexDirection: 'row',
    gap: 16,
  },
  quickActionCard: {
    flex: 1,
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  quickActionIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 32,
    color: theme.primary,
  },
  quickActionLabel: {
    color: theme.text,
    fontSize: 14,
    fontWeight: '500',
  },
  quickActionCount: {
    color: theme.textMuted,
    fontSize: 24,
    fontWeight: '700',
  },

  // Template Grid
  templateGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  templateCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 12,
    borderWidth: 2,
    borderColor: theme.borderLight,
    position: 'relative',
  } as any,
  templateCardSelected: {
    borderColor: theme.primary,
    backgroundColor: 'rgba(255, 158, 128, 0.1)',
  },
  previewImageContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.background,
    marginBottom: 8,
  },
  previewImage: {
    width: '100%',
    height: '100%',
  },
  templateInfo: {
    alignItems: 'center',
  },
  templateName: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.text,
  },
  templateNameSelected: {
    color: theme.primary,
  },
  selectedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  selectedIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },

  // Font Size Panel
  fontSizePanel: {
    padding: 24,
  },
  fontSizePreview: {
    alignItems: 'center',
    marginBottom: 24,
    paddingVertical: 16,
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: 12,
  },
  fontSizePreviewText: {
    fontWeight: '700',
    color: theme.text,
    marginBottom: 4,
  },
  fontSizePreviewLabel: {
    color: theme.textMuted,
  },
  fontSizeControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  fontSizeButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  } as any,
  fontSizeButtonDisabled: {
    opacity: 0.4,
  },
  fontSizeButtonText: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.primary,
  },
  fontSizeButtonTextLarge: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.primary,
  },
  fontSizeButtonTextDisabled: {
    color: theme.textMuted,
  },
  fontScaleValue: {
    alignItems: 'center',
    minWidth: 60,
  },
  fontScaleValueText: {
    fontSize: 18,
    color: theme.text,
    fontWeight: '600',
  },

  // Cycle Panel
  cyclePanel: {
    padding: 20,
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toggleLabel: {
    color: theme.text,
    fontSize: 16,
  },
  toggle: {
    width: 52,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: 2,
    justifyContent: 'center',
  },
  toggleEnabled: {
    backgroundColor: theme.primary,
  },
  toggleKnob: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#fff',
  },
  toggleKnobEnabled: {
    alignSelf: 'flex-end',
  },
  intervalRow: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  intervalLabel: {
    color: theme.textMuted,
    fontSize: 14,
    marginBottom: 12,
  },
  intervalButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  intervalButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
  },
  intervalButtonSelected: {
    backgroundColor: theme.primary,
  },
  intervalButtonText: {
    color: theme.textMuted,
    fontSize: 14,
    fontWeight: '500',
  },
  intervalButtonTextSelected: {
    color: '#fff',
  },
});
